/**
 * eventAttention.js — Event-level attention/priority engine.
 *
 * Rolls up module intelligence signals into a single event attention state.
 * Works at two tiers:
 *
 *   FULL (computeEventAttention)
 *     Consumes module signals from moduleIntelligence.js.
 *     Used by EventWorkspacePage where all module data is loaded.
 *
 *   LIGHTWEIGHT (computeEventAttentionLight)
 *     Works from basic event list fields (pipelineStage, eventDate, etc.)
 *     Used by MyControlCenter, Dashboard, and any multi-event surface
 *     that doesn't fetch per-event module data.
 *
 * ATTENTION MODEL:
 *   {
 *     level:         'healthy' | 'active' | 'warning' | 'critical'
 *     color:         hex color string
 *     message:       short human-readable summary
 *     priorityScore: 0–100 (higher = more urgent)
 *     sourceModules: string[] (which modules drive the state, full tier only)
 *   }
 */
import { parseLocalDate } from '../utils/dateFormatters';

const ATTENTION_LEVELS = {
  healthy:  { color: '#22C55E', score: 0 },
  active:   { color: '#3B82F6', score: 25 },
  warning:  { color: '#F59E0B', score: 60 },
  critical: { color: '#EF4444', score: 85 },
};

function attention(level, message, sourceModules = []) {
  const base = ATTENTION_LEVELS[level];
  return {
    level,
    color: base.color,
    message,
    priorityScore: base.score,
    sourceModules,
  };
}

/* ══════════════════════════════════════════════════════════
 *  FULL TIER — from module intelligence signals
 * ══════════════════════════════════════════════════════════ */

// Human-friendly module names + where to find them, so the badge can tell
// the admin exactly WHAT is missing and WHERE to fix it (hover list). Keys
// match the moduleSignals keys AND the workspace tab keys, so each item is
// clickable straight to its tab.
const MODULE_LABELS = {
  proposal:  'Proposal',
  contract:  'Contract',
  planning:  'Planning',
  logistics: 'Logistics',
  staffing:  'Staffing',
  timeline:  'Timeline',
  follow_up: 'Follow-Up',
  payments:  'Payments',
};
// Modules the admin can't act on from the workspace tabs. `audit` is a
// read-only trail ("not started" just means no rows yet) and `control`
// is the live event-day meta module — neither is ever "missing info"
// the admin should be nagged about.
const META_MODULE_KEYS = new Set(['audit', 'control']);

// Turn signal entries into pinpointed items: { key, label, message }.
// `key` doubles as the tab to open when the item is clicked.
function buildItems(entries) {
  return entries.map(([k, s]) => ({
    key: k,
    label: MODULE_LABELS[k] || k,
    message: s.message || 'Needs info',
  }));
}

/**
 * Compute event-level attention from module signals.
 * @param {Object} moduleSignals — output of getAllModuleSignals()
 *   e.g. { proposal: { level, color, message, priority }, ... }
 * @param {Object} [opts]
 * @param {number|null} [opts.daysUntil] — days until the event (null if unknown).
 *   Used to GATE tone: unstarted modules are normal on a far-off event, so we
 *   keep them neutral (blue) until the event is near (≤7 days), then elevate
 *   to amber. Genuine problems (needs-review, critical-missing) stay amber/red
 *   regardless of date.
 * @returns attention object, augmented with `items: [{key,label,message}]`
 */
export const computeEventAttention = (moduleSignals, opts = {}) => {
  const { daysUntil = null } = opts;
  if (!moduleSignals || Object.keys(moduleSignals).length === 0) {
    return attention('active', 'No module data');
  }

  // Only actionable modules drive the badge. Meta modules (audit/control)
  // are excluded entirely; payments is excluded only in its 'missing'
  // state (pricing never set = a deliberate choice on events run outside
  // the payment system — the locked module can't be skipped to silence
  // it). A REAL payments problem (balance outstanding → warning) still
  // surfaces normally.
  const entries = Object.entries(moduleSignals).filter(([k, s]) => {
    if (META_MODULE_KEYS.has(k)) return false;
    if (k === 'payments' && s.level === 'missing') return false;
    return true;
  });
  const warnings = entries.filter(([, s]) => s.level === 'warning');
  const missing = entries.filter(([, s]) => s.level === 'missing');
  const active = entries.filter(([, s]) => s.level === 'active');
  const complete = entries.filter(([, s]) => s.level === 'complete');
  const highPriority = entries.filter(([, s]) => s.priority === 'high');

  const isNear = daysUntil != null && daysUntil <= 7;

  // Critical: real problems — high-priority warnings or multiple warnings.
  // These are always urgent, date aside.
  if (highPriority.length > 0 || warnings.length >= 2) {
    const src = [...new Map([...warnings, ...highPriority]).entries()];
    const items = buildItems(src);
    const msg = items.length > 1 ? `Needs attention · ${items.length} items` : (items[0]?.message || 'Needs attention');
    const result = attention('critical', msg, src.map(([k]) => k));
    result.items = items;
    result.priorityScore += highPriority.length * 10;
    return result;
  }

  // A single genuine warning → amber, pinpointed to the one module.
  if (warnings.length === 1) {
    const items = buildItems(warnings);
    const r = attention('warning', `${items[0].label}: ${items[0].message}`, [warnings[0][0]]);
    r.items = items;
    return r;
  }

  // Not-started / missing modules. This is a NORMAL early-stage state, so keep
  // it calm (neutral blue "active") and only escalate to amber when the event
  // is near. Either way we carry the exact items so the badge can list them.
  if (missing.length >= 1) {
    const items = buildItems(missing);
    const level = isNear ? 'warning' : 'active';
    const label = items.length === 1 ? 'item' : 'items';
    const r = attention(level, `Missing info · ${items.length} ${label}`, missing.map(([k]) => k));
    r.items = items;
    return r;
  }

  // Healthy: all complete
  if (complete.length === entries.length) {
    return attention('healthy', 'All modules complete');
  }

  // Active: work in progress (nothing missing, nothing wrong)
  if (active.length > 0) {
    return attention('active', `${complete.length}/${entries.length} complete`, active.map(([k]) => k));
  }

  return attention('healthy', 'On track');
};

/* ══════════════════════════════════════════════════════════
 *  LIGHTWEIGHT TIER — from event list fields
 * ══════════════════════════════════════════════════════════ */

/**
 * Pipeline stage → attention level mapping.
 * Stages that need action or have risk are elevated.
 */
const STAGE_ATTENTION = {
  new_lead:            { level: 'active',  msg: 'New lead' },
  new_inquiry:         { level: 'active',  msg: 'New enquiry' },
  call_requested:      { level: 'warning', msg: 'Call requested' },
  intake_complete:     { level: 'active',  msg: 'Intake done' },
  under_review:        { level: 'active',  msg: 'Under review' },
  reviewing:           { level: 'active',  msg: 'Reviewing' },
  contacted:           { level: 'active',  msg: 'Contacted' },
  consult_scheduled:   { level: 'active',  msg: 'Consult set' },
  proposal_ready:      { level: 'active',  msg: 'Proposal ready' },
  proposal_sent:       { level: 'active',  msg: 'Proposal sent' },
  proposal_revision_requested: { level: 'warning', msg: 'Revision needed' },
  proposal_approved:   { level: 'active',  msg: 'Approved, needs contract' },
  contract_ready:      { level: 'active',  msg: 'Contract ready' },
  contract_sent:       { level: 'active',  msg: 'Contract sent' },
  awaiting_signature:  { level: 'active',  msg: 'Awaiting signature' },
  booked:              { level: 'healthy', msg: 'Booked' },
  planning_active:     { level: 'active',  msg: 'Planning active' },
  ready_for_execution: { level: 'healthy', msg: 'Ready' },
  lost:                { level: 'critical', msg: 'Lost' },
  followup_overdue:    { level: 'warning', msg: 'Overdue follow-up' },
};

/**
 * Compute attention from basic event list fields.
 * @param {Object} event — event object from list API (has pipelineStage, eventDate, etc.)
 * @returns attention object (sourceModules will be empty)
 */
export const computeEventAttentionLight = (event) => {
  const stageKey = event.effectiveStage || event.pipelineStage || event.leadStatus?.toLowerCase().replace(/\s+/g, '_');
  const stageInfo = STAGE_ATTENTION[stageKey] || { level: 'active', msg: stageKey || 'In progress' };
  const result = attention(stageInfo.level, stageInfo.msg);

  // Date proximity boost
  const eventDate = parseLocalDate(event.eventDate || event.date);
  if (eventDate) {
    const now = new Date();
    const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 0) result.priorityScore += 25;       // Today or past
    else if (daysUntil <= 3) result.priorityScore += 15;   // Within 3 days
    else if (daysUntil <= 7) result.priorityScore += 5;    // Within a week
  }

  return result;
};
